import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import Link from 'next/link'

dayjs.extend(utc)

/**
 * data 里的 jsonb datetime 存的是「裸 UTC」(如 `2026-09-21 04:59:37.049`)。
 * 后端会按 Rails 口径补上 +08:00 再下发，但一旦哪条路径漏了格式化，
 * dayjs 会把裸串当本地时间 → 算出来是 1 小时前 → 横幅直接显示「已到期」。
 * 这里按有无时区标记分流，少一个后端漏格式化也不会显示错。
 */
const parseExpiry = (raw: string) => (/([+-]\d\d:?\d\d|Z)$/.test(raw) ? dayjs(raw) : dayjs.utc(raw).local())

/**
 * 测试赛限时横幅。
 *
 * 测试赛创建后 test_expires_at 到点会被 DeleteExpiredTestMatchesJob 软删除，
 * 用户必须在每个页面都能一眼看到还剩多久，否则会像以前那样拿测试赛当正式赛办、
 * 最后成绩全丢。只有 test_expires_at 有值时才渲染（机制上线前的老测试赛没有该字段，
 * 不会被删除，也就不该吓唬用户）。
 */
const TestMatchBanner: React.FC<{ match: { is_test_match?: boolean; test_expires_at?: string } }> = ({ match }) => {
  const expiresAtRaw = match?.is_test_match ? match?.test_expires_at : undefined
  const [now, setNow] = useState(() => dayjs())

  useEffect(() => {
    if (!expiresAtRaw) return undefined
    const id = setInterval(() => setNow(dayjs()), 1000)
    return () => clearInterval(id)
  }, [expiresAtRaw])

  if (!expiresAtRaw) return null

  const expiresAt = parseExpiry(expiresAtRaw)

  const seconds = Math.max(0, expiresAt.diff(now, 'second'))
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0')
  const ss = String(seconds % 60).padStart(2, '0')
  const urgent = seconds <= 300

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-x-0 border-b border-t-0 border-solid px-4 py-2 text-sm ${
        urgent ? 'border-red-300 bg-red-50 text-red-800' : 'border-amber-300 bg-amber-50 text-amber-900'
      }`}
    >
      <span className="font-semibold">测试赛</span>
      <span>
        {seconds > 0 ? (
          <>
            将在 <b className="tabular-nums">{`${mm}:${ss}`}</b> 后自动删除（{expiresAt.format('HH:mm')}）
          </>
        ) : (
          <>已到期，即将被自动删除</>
        )}
      </span>
      <span className="text-xs opacity-80">测试赛不可转为正式比赛，删除后成绩无法在前台找回</span>
      <Link href="/match" className="ml-auto underline">
        去创建正式比赛 →
      </Link>
    </div>
  )
}

export default TestMatchBanner
